import React, { FC } from 'react'

import { colors, Typography } from '@smarty-nx/ds'
import { TRACKING_CATEGORIES } from '@smarty-nx/tracking'
import { graphql, HeadProps } from 'gatsby'

import classes from './index.module.scss'
import Card from '../../components/Card'
import ChatDetails from '../../components/ChatDetails'
import ChristmasOpeningHours from '../../components/ChristmasOpeningHours'
import EllipseCutOut from '../../components/EllipseCutOut'
import HelpTopics from '../../components/HelpTopics'
import PageHead from '../../components/PageHead'
import Search from '../../components/Search'
import { FullWidthContainer, SiteContainer } from '../../components/shared'
import paddingClasses from '../../components/styles/padding.module.scss'
import withApp from '../../components/withApp/withApp'
import { FEATURES } from '../../config'
import {
  createFAQPageSchema,
  jsonLdOrganization,
  mapFaqItemsToSchemaItems,
} from '../../constants/structuredData'
import FaqSection from '../../modular-system/FaqSection'
import { FaqSectionProps } from '../../modular-system/FaqSection/FaqSection'
import { obfuscateEmail } from '../../utils/obfuscateEmail'

interface ContactProps {
  data: {
    contentfulPageSection: FaqSectionProps
  }
  location: { pathname: string }
}

const contactContent = {
  title: 'Contact us',
  seo: {
    title: 'Contact us - FAQ',
    description: '',
  },
  PR: {
    title: 'PR and Media Enquiries',
    text: (
      <Typography
        variant="Kollektif/Small"
        dataTestId="pr-text"
        className={classes.prText}
      >
        If you&apos;d like to talk to our press team about anything, please
        contact us at:
        <br />
        {obfuscateEmail(
          'SMARTYmedia',
          'smarts.agency',
          'pr-media-enquiries-link'
        )}
      </Typography>
    ),
  },
  searchTitle: 'Or search help articles',
  chat: {
    title: 'How to get more help',
  },
  complaints: {
    title: 'Making a complaint',
    text: (
      <Typography
        variant="Kollektif/Small"
        dataTestId="complaints-text"
        className={classes.prText}
      >
        If you need to make a complaint, please initially use our web chat to
        see if we can help.
        <br />
        To escalate an issue, please fill in a complaints form by going here:
        <br />
        <a
          href="/complaints"
          data-item-category={TRACKING_CATEGORIES.NAVIGATION}
          data-item-name="complaints-link"
          data-testid="complaints-link"
        >
          smarty.co.uk/complaints
        </a>
      </Typography>
    ),
  },
}

const Contact: FC<ContactProps> = ({
  data: {
    contentfulPageSection: { header, items, link },
  },
}) => (
  <>
    <FullWidthContainer
      className={classes.sectionContainer}
      background={colors['Brand/Yellow']}
      color={colors['Brand/PrimaryBlack']}
    >
      <EllipseCutOut color={colors['UI/DarkGrey']} isTopCutout />
      <SiteContainer>
        <Typography
          component="h1"
          variant="Kollektif/Mega"
          className={classes.title}
          dataTestId="contact-page-title"
        >
          {contactContent.title}
        </Typography>

        <Card className={classes.card}>
          <HelpTopics />
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            className={classes.searchHeading}
            dataTestId="search-title"
          >
            {contactContent.searchTitle}
          </Typography>
          <Search />
        </Card>

        <div className={paddingClasses.paddingMd}>
          <FaqSection darkText header={header} items={items} link={link} />
        </div>

        <div className={paddingClasses.paddingBottomMd}>
          <Card>
            <Typography
              component="h2"
              variant="Kollektif/ExtraLarge"
              className={classes.sectionHeading}
              dataTestId="other-ways-title"
            >
              {contactContent.chat.title}
            </Typography>
            <ChatDetails />
          </Card>
        </div>

        {FEATURES.CHRISTMAS_OPENING_HOURS && (
          <div className={paddingClasses.paddingBottomMd}>
            <ChristmasOpeningHours />
          </div>
        )}
      </SiteContainer>
    </FullWidthContainer>

    <FullWidthContainer
      className={classes.sectionContainer}
      background={colors['UI/DarkGrey']}
    >
      <div className={paddingClasses.paddingMd}>
        <SiteContainer>
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            className={classes.subtitle}
            dataTestId="pr-title"
          >
            {contactContent.PR.title}
          </Typography>
          {contactContent.PR.text}
        </SiteContainer>
      </div>
    </FullWidthContainer>

    <FullWidthContainer
      className={classes.sectionContainer}
      background={colors['UI/White']}
      color={colors['Brand/PrimaryBlack']}
    >
      <div className={paddingClasses.paddingMd}>
        <SiteContainer>
          <Typography
            component="h2"
            variant="Kollektif/ExtraLarge"
            className={classes.subtitle}
            dataTestId="complaints-title"
          >
            {contactContent.complaints.title}
          </Typography>
          {contactContent.complaints.text}
        </SiteContainer>
      </div>
    </FullWidthContainer>
  </>
)

export default withApp(Contact)

export const Head: FC<HeadProps<ContactProps['data']>> = ({
  data,
  location,
}) => {
  const faqSchema = createFAQPageSchema(
    mapFaqItemsToSchemaItems(data.contentfulPageSection?.items || [])
  )

  return (
    <PageHead
      title={contactContent.seo.title}
      description={contactContent.seo.description}
      location={location}
    >
      <script type="application/ld+json">
        {JSON.stringify(jsonLdOrganization)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </PageHead>
  )
}

export const query = graphql`
  query {
    contentfulPageSection(sectionName: { eq: "contact-faq-list" }) {
      ...FaqSection
    }
  }
`
